// src/components/SalaryManagement.jsx
// Complete Salary Management with Attendance-Based Calculations - Blue Theme
// Colors: #0D47A1 (primary dark blue), #2196F3 (primary blue), 
// #90CAF9 (light blue), #E3F2FD (very light blue)

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSchoolName, getSchoolEmailDomain } from "./schoolNameStore";
import { ArrowRight, BarChart3, Coins, ClipboardList, FileEdit, CheckCircle2, XCircle } from "lucide-react";
export default function SalaryManagement() {
  const navigate = useNavigate();
  const schoolName = useSchoolName();

  // ========== STATE VARIABLES ==========
  const [employees, setEmployees] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  });
  const [activeTab, setActiveTab] = useState("payments");
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [historyTypeFilter, setHistoryTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ========== MODALS STATE ==========
  const [payModalData, setPayModalData] = useState(null);
  const [payslipData, setPayslipData] = useState(null);
  const [editSalaryData, setEditSalaryData] = useState(null);
  const [attendanceDetailData, setAttendanceDetailData] = useState(null);
  const [showWhatsAppToast, setShowWhatsAppToast] = useState(false);
  const [whatsAppToastMsg, setWhatsAppToastMsg] = useState('');

  // ========== PAY FORM FIELDS ==========
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Bank Transfer");
  const [payTxId, setPayTxId] = useState("");
  const [payRemarks, setPayRemarks] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);

  // ========== BULK PAYMENT FIELDS ==========
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [showBulkPayModal, setShowBulkPayModal] = useState(false);
  const [bulkPayMethod, setBulkPayMethod] = useState("Bank Transfer");
  const [bulkPayRemarks, setBulkPayRemarks] = useState("");
  const [processingBulkPay, setProcessingBulkPay] = useState(false);

  // ========== EDIT SALARY FIELDS ==========
  const [newSalary, setNewSalary] = useState("");
  const [savingSalary, setSavingSalary] = useState(false);

  // ========== FETCH DATA ==========
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setSelectedEmpIds([]);
    try {
      const empRes = await fetch(`https://schoolcrm-fzcv.onrender.com/api/salaries/employees?month=${selectedMonth}`);
      if (!empRes.ok) throw new Error("Failed to fetch employees salary status");
      const empData = await empRes.json();
      setEmployees(empData);
      const txRes = await fetch(`https://schoolcrm-fzcv.onrender.com/api/salaries/transactions?month=${selectedMonth}`);
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "An error occurred while loading data");
    } finally {
      setLoading(false);
    }
  };
  const fetchAllTransactions = async () => {
    try {
      const txRes = await fetch(`https://schoolcrm-fzcv.onrender.com/api/salaries/transactions`);
      if (txRes.ok) {
        const txData = await txRes.json();
        setTransactions(txData);
      }
    } catch (err) {
      console.error("Error fetching all transactions:", err);
    }
  };
  useEffect(() => {
    fetchData();
  }, [selectedMonth]);
  useEffect(() => {
    if (activeTab === "history") {
      fetchAllTransactions();
    } else if (activeTab === "payments" || activeTab === "config") {
      fetchData();
    }
  }, [activeTab]);

  // ========== ATTENDANCE DETAILS FETCH ==========
  const fetchAttendanceDetails = async (employeeId, employeeType) => {
    try {
      const res = await fetch(`https://schoolcrm-fzcv.onrender.com/api/salaries/employee/${employeeId}/attendance-salary?month=${selectedMonth}&type=${employeeType}`);
      if (res.ok) {
        const data = await res.json();
        setAttendanceDetailData(data);
      } else {
        const errData = await res.json();
        alert(errData.message || "Failed to fetch attendance details");
      }
    } catch (err) {
      console.error("Error fetching attendance details:", err);
      alert("Network error fetching attendance details");
    }
  };

  // ========== PAYMENT HANDLERS ==========
  const handlePaySubmit = async e => {
    e.preventDefault();
    if (!payModalData) return;
    setProcessingPayment(true);
    try {
      const finalAmount = payModalData.calculatedSalary || payAmount || payModalData.baseSalary;
      const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/salaries/pay", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          employeeId: payModalData._id,
          employeeType: payModalData.type,
          baseSalary: payModalData.baseSalary,
          paidAmount: Number(finalAmount),
          calculatedSalary: Number(finalAmount),
          month: selectedMonth,
          paymentMethod: payMethod,
          transactionId: payTxId,
          remarks: payRemarks
        })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.message || "Payment processing failed");
        return;
      }
      setPayModalData(null);
      setPayAmount("");
      setPayMethod("Bank Transfer");
      setPayTxId("");
      setPayRemarks("");
      fetchData();
      alert("Salary payment recorded successfully!");
    } catch (err) {
      console.error(err);
      alert("Network error. Please try again.");
    } finally {
      setProcessingPayment(false);
    }
  };
  const handleBulkPaySubmit = async e => {
    e.preventDefault();
    if (selectedEmpIds.length === 0) return;
    setProcessingBulkPay(true);
    try {
      const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/salaries/bulk-pay", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          employeeIds: selectedEmpIds,
          month: selectedMonth,
          paymentMethod: bulkPayMethod,
          remarks: bulkPayRemarks
        })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.message || "Bulk payment failed");
        return;
      }
      setShowBulkPayModal(false);
      setSelectedEmpIds([]);
      setBulkPayMethod("Bank Transfer");
      setBulkPayRemarks("");
      fetchData();
      alert(`Bulk payment successful for ${data.transactions?.length || selectedEmpIds.length} employees!`);
    } catch (err) {
      console.error(err);
      alert("Network error processing bulk payment");
    } finally {
      setProcessingBulkPay(false);
    }
  };
  const handleSalarySave = async e => {
    e.preventDefault();
    if (!editSalaryData) return;
    setSavingSalary(true);
    try {
      const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/salaries/set-salary", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          employeeId: editSalaryData._id,
          employeeType: editSalaryData.type,
          salary: Number(newSalary)
        })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.message || "Failed to update base salary");
        return;
      }
      setEditSalaryData(null);
      setNewSalary("");
      fetchData();
      alert("Base salary updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Network error updating base salary");
    } finally {
      setSavingSalary(false);
    }
  };

  // ========== WHATSAPP SHARE FUNCTION ==========
  const handleWhatsAppShare = emp => {
    const month = formatMonth(selectedMonth);
    const baseSalary = Number(emp.baseSalary || 0).toLocaleString('en-IN');
    const paidAmount = Number(emp.paymentDetails?.paidAmount || emp.calculatedSalary || emp.baseSalary || 0).toLocaleString('en-IN');
    const receiptDate = emp.paymentDetails?.paymentDate ? new Date(emp.paymentDetails.paymentDate).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }) : new Date().toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const paymentMethod = emp.paymentDetails?.paymentMethod || 'Bank Transfer';
    const transactionId = emp.paymentDetails?.transactionId || 'N/A';
    const attendance = emp.attendance || {
      present: 0,
      late: 0,
      leave: 0,
      absent: 0
    };
    const deductions = emp.deductions || {
      lateDeduction: 0,
      leaveDeduction: 0,
      absentDeduction: 0,
      totalDeductions: 0
    };
    const message = `*${schoolName.toUpperCase()} SMART CAMPUS*
Affiliated Secondary & Higher Secondary School
Salem, Tamil Nadu, India | Contact: support@${getSchoolEmailDomain(schoolName)}

*SALARY ADVICE SLIP - ${month.toUpperCase()}*

*Employee Name:* ${emp.name}
*Designation/Role:* ${emp.role}
*Salary Month:* ${month}
*Receipt Date:* ${receiptDate}
 *Employee Category:* ${emp.type}
*Payment Mode:* ${paymentMethod}

━━━━━━━━━━━━━━━━━━━━━━━━━
*ATTENDANCE SUMMARY*
Present: ${attendance.present || 0} days
⏰ Late: ${attendance.late || 0} days
Leave: ${attendance.leave || 0} days
Absent: ${attendance.absent || 0} days

*DEDUCTIONS*
Late Deduction (10% daily): ₹${Number(deductions.lateDeduction || 0).toLocaleString('en-IN')}
Leave Deduction (100%): ₹${Number(deductions.leaveDeduction || 0).toLocaleString('en-IN')}
Absent Deduction (100%): ₹${Number(deductions.absentDeduction || 0).toLocaleString('en-IN')}
━━━━━━━━━━━━━━━━━━━━━━━━━

*Base Salary:* ₹${baseSalary}
*Total Deductions:* ₹${Number(deductions.totalDeductions || 0).toLocaleString('en-IN')}
*Net Disbursed Amount:* ₹${paidAmount}
🆔 *Transaction ID:* ${transactionId}
*Status:* PAID & SETTLED
━━━━━━━━━━━━━━━━━━━━━━━━━

_This is a system-generated payslip._
_For queries: support@${getSchoolEmailDomain(schoolName)}_`;
    let phone = emp.phone || '';
    phone = phone.replace(/[^+\d]/g, '');
    if (!phone) {
      setWhatsAppToastMsg(" No phone number for this employee");
      setShowWhatsAppToast(true);
      setTimeout(() => setShowWhatsAppToast(false), 3000);
      return;
    }
    const encodedMsg = encodeURIComponent(message);
    const waUrl = `https://wa.me/${phone}?text=${encodedMsg}`;
    const newWindow = window.open(waUrl, '_blank');
    if (!newWindow || newWindow.closed) {
      window.location.href = waUrl;
    }
    setWhatsAppToastMsg(`Sharing payslip with ${emp.name}`);
    setShowWhatsAppToast(true);
    setTimeout(() => setShowWhatsAppToast(false), 3000);
  };

  // ========== HELPER FUNCTIONS ==========
  const formatMonth = mStr => {
    if (!mStr) return "";
    const [year, month] = mStr.split("-");
    const date = new Date(year, parseInt(month) - 1, 1);
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric"
    });
  };

  // ========== CALCULATIONS ==========
  const totalPayroll = employees.reduce((sum, emp) => sum + (emp.baseSalary || 0), 0);
  const totalCalculated = employees.reduce((sum, emp) => sum + (emp.calculatedSalary || emp.baseSalary || 0), 0);
  const totalPaid = employees.reduce((sum, emp) => sum + (emp.status === "Paid" ? emp.paymentDetails?.paidAmount || 0 : 0), 0);
  const remainingPay = totalCalculated - totalPaid;
  const paidCount = employees.filter(emp => emp.status === "Paid").length;
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) || emp.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "All" || emp.type === typeFilter;
    const matchesStatus = statusFilter === "All" || emp.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });
  const filteredTransactions = transactions.filter(tx => historyTypeFilter === "All" || tx.employeeType === historyTypeFilter);

  // ========== RENDER ==========
  return <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Inter:wght@300;400;500;600;700;800&display=swap');
        
        * { box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; background-color: #FFFFFF; }
        
        .sal-card { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); background: #FFFFFF; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
        .sal-card:hover { transform: translateY(-3px); box-shadow: 0 12px 24px rgba(13,71,161,0.08); }
        .t-row:hover { background-color: #E3F2FD !important; }
        .badge-staff { background: #0D47A1; color: #FFFFFF; }
        .badge-worker { background: #2196F3; color: #FFFFFF; }
        .badge-paid { background: #0D47A1; color: #FFFFFF; }
        .badge-unpaid { background: #E3F2FD; color: #0D47A1; border: 1px solid #0D47A1; }
        .badge-deduction { background: #E3F2FD; color: #2196F3; }
        
        .tab-btn {
          border: 1px solid #90CAF9;
          background: #FFFFFF;
          color: #0D47A1;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .tab-btn.active {
          background: #0D47A1;
          border-color: #0D47A1;
          color: #FFFFFF;
          box-shadow: 0 2px 8px rgba(13,71,161,0.35);
        }
        .tab-btn:hover {
          background: #E3F2FD;
        }
        
        .whatsapp-share-btn {
          background: #25D366;
          color: white;
          border: none;
          padding: 6px 14px;
          border-radius: 30px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: 0.15s;
          box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
        }
        .whatsapp-share-btn:hover {
          background: #1da85c;
          transform: scale(0.96);
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .deduction-row {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
          font-size: 12px;
        }
        .deduction-total {
          font-weight: 700;
          color: #0D47A1;
        }
        .calculated-salary {
          font-size: 14px;
          font-weight: 700;
          color: #0D47A1;
        }
        .attendance-badge {
          display: inline-block;
          padding: 1px 8px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 600;
          margin-right: 4px;
        }
        .attendance-present { background: #0D47A1; color: #FFFFFF; }
        .attendance-late { background: #E3F2FD; color: #2196F3; }
        .attendance-leave { background: #E3F2FD; color: #2196F3; }
        .attendance-absent { background: #E3F2FD; color: #2196F3; }
        
        .blue-gradient { background: linear-gradient(135deg, #0D47A1, #2196F3); }
        .blue-gradient-light { background: linear-gradient(135deg, #2196F3, #0D47A1); }
        
        input:focus, select:focus, textarea:focus { box-shadow: 0 0 0 2px rgba(13,71,161,0.15); }

        .pay-modal-body {
          display: flex;
          flex-direction: row;
        }
        .pay-modal-sidebar {
          width: 210px;
          flex-shrink: 0;
          background: #E3F2FD;
          padding: 20px 16px;
          border-right: 1px solid #90CAF9;
        }
        .pay-modal-main {
          flex: 1;
          padding: 20px;
          min-width: 0;
        }
        @media (max-width: 560px) {
          .pay-modal-body { flex-direction: column; }
          .pay-modal-sidebar { width: 100%; border-right: none; border-bottom: 1px solid #90CAF9; }
        }
      `}</style>

      {/* ===== WHATSAPP TOAST ===== */}
      {showWhatsAppToast && <div style={{
      position: 'fixed',
      bottom: '30px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#0D47A1',
      color: 'white',
      padding: '12px 24px',
      borderRadius: '50px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'fadeIn 0.3s ease',
      fontFamily: "'Inter', sans-serif"
    }}>
          <i className="fab fa-whatsapp" style={{
        color: '#25D366',
        fontSize: '20px'
      }}></i>
          {whatsAppToastMsg}
        </div>}

      <div style={{
      minHeight: "100vh",
      backgroundColor: "#FFFFFF",
      paddingBottom: 60
    }}>
        {/* ===== HEADER ===== */}
        <div className="blue-gradient" style={{
        padding: "18px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 16
        }}>
            <button onClick={() => navigate("/admin")} style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            borderRadius: 10,
            width: 42,
            height: 42,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#fff",
            fontSize: 18
          }} title="Back to Admin">
              <i className="fas fa-arrow-left"></i>
            </button>
            <div>
              <div style={{
              fontWeight: 800,
              fontSize: 20,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 8,
              letterSpacing: "-0.5px",
              fontFamily: "'Poppins', sans-serif"
            }}>
                <i className="fas fa-wallet" style={{
                color: "#90CAF9"
              }}></i> Salary &amp; Payroll Management
              </div>
              <div style={{
              fontSize: 11,
              color: "#90CAF9",
              marginTop: 2,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontWeight: 600
            }}>Attendance-Based Salary with Deductions</div>
            </div>
          </div>
          <div style={{
          display: "flex",
          gap: 10,
          alignItems: "center"
        }} />
        </div>

        <div style={{
        width: "100%",
        padding: "0 24px",
        marginTop: 24
      }}>
          
          {/* ===== MONTH SELECTOR & TABS ===== */}
          <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          backgroundColor: "#FFFFFF",
          padding: "16px 20px",
          borderRadius: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          marginBottom: 24
        }}>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12
          }}>
              <label style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#0D47A1",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>Payroll Month:</label>
              <input type="month" value={selectedMonth} onChange={e => {
              if (e.target.value) setSelectedMonth(e.target.value);
            }} style={{
              background: "#FFFFFF",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 13,
              fontWeight: 600,
              color: "#0D47A1",
              outline: "none",
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }} />
            </div>
            
            <div style={{
            display: "flex",
            background: "#E3F2FD",
            borderRadius: 12,
            padding: 4,
            flexWrap: "wrap",
            gap: 4
          }}>
              {[{
              id: "payments",
              label: "Salary Payments",
              icon: "fas fa-money-bill-wave"
            }, {
              id: "history",
              label: "Transaction History",
              icon: "fas fa-file-invoice-dollar"
            }, {
              id: "config",
              label: "Salary Settings",
              icon: "fas fa-coins"
            }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}>
                  <i className={tab.icon}></i> {tab.label}
                </button>)}
            </div>
          </div>

          {/* ===== STATS SECTION ===== */}
          {activeTab !== "history" && <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24
        }}>
              <div className="sal-card" style={{
            padding: 20,
            borderRadius: 16
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
                  <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#2196F3",
                textTransform: "uppercase"
              }}>Total Base Payroll</span>
                  <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#0D47A1",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                    <i className="fas fa-coins"></i>
                  </div>
                </div>
                <div style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#0D47A1",
              marginTop: 12,
              fontFamily: "'Poppins', sans-serif"
            }}>
                  ₹{totalPayroll.toLocaleString("en-IN")}
                </div>
                <div style={{
              fontSize: 11,
              color: "#2196F3",
              marginTop: 4
            }}>Base salary for all employees</div>
              </div>

              <div className="sal-card" style={{
            padding: 20,
            borderRadius: 16
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
                  <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#2196F3",
                textTransform: "uppercase"
              }}>Calculated Payroll</span>
                  <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#0D47A1",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                    <i className="fas fa-calculator"></i>
                  </div>
                </div>
                <div style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#0D47A1",
              marginTop: 12,
              fontFamily: "'Poppins', sans-serif"
            }}>
                  ₹{totalCalculated.toLocaleString("en-IN")}
                </div>
                <div style={{
              fontSize: 11,
              color: "#2196F3",
              marginTop: 4,
              fontWeight: 600
            }}>
                  After attendance deductions
                </div>
              </div>

              <div className="sal-card" style={{
            padding: 20,
            borderRadius: 16
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
                  <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#2196F3",
                textTransform: "uppercase"
              }}>Disbursed Amount</span>
                  <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#0D47A1",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                    <i className="fas fa-check"></i>
                  </div>
                </div>
                <div style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#0D47A1",
              marginTop: 12,
              fontFamily: "'Poppins', sans-serif"
            }}>
                  ₹{totalPaid.toLocaleString("en-IN")}
                </div>
                <div style={{
              fontSize: 11,
              color: "#2196F3",
              marginTop: 4,
              fontWeight: 600
            }}>
                  {paidCount} of {employees.length} employees paid
                </div>
              </div>

              <div className="sal-card" style={{
            padding: 20,
            borderRadius: 16
          }}>
                <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
                  <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#2196F3",
                textTransform: "uppercase"
              }}>Pending Balance</span>
                  <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#E3F2FD",
                color: "#0D47A1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                    <i className="fas fa-clock"></i>
                  </div>
                </div>
                <div style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#0D47A1",
              marginTop: 12,
              fontFamily: "'Poppins', sans-serif"
            }}>
                  ₹{remainingPay.toLocaleString("en-IN")}
                </div>
                <div style={{
              fontSize: 11,
              color: "#2196F3",
              marginTop: 4
            }}>
                  {employees.length - paidCount} employees unpaid
                </div>
              </div>
            </div>}

          {/* ===== TAB CONTENT ===== */}
          {loading ? <div style={{
          textAlign: "center",
          padding: "60px 0",
          background: "#FFFFFF",
          borderRadius: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}>
              <div style={{
            fontSize: 32,
            color: "#0D47A1",
            animation: "spin 1s linear infinite"
          }}>
                <i className="fas fa-circle-notch"></i>
              </div>
              <div style={{
            marginTop: 16,
            fontSize: 14,
            color: "#2196F3",
            fontWeight: 500
          }}>Loading employee salary records...</div>
            </div> : error ? <div style={{
          background: "#FFFFFF",
          padding: "40px 24px",
          borderRadius: 16,
          textAlign: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}>
              <div style={{
            fontSize: 40,
            color: "#0D47A1",
            marginBottom: 12
          }}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div style={{
            fontSize: 15,
            fontWeight: 700,
            color: "#0D47A1"
          }}>Error fetching salary details</div>
              <div style={{
            fontSize: 12,
            color: "#2196F3",
            marginTop: 4
          }}>{error}</div>
              <button onClick={fetchData} style={{
            marginTop: 16,
            padding: "8px 16px",
            background: "#0D47A1",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer"
          }}>Retry</button>
            </div> : <>
              {/* ===== TAB 1: PAYMENTS ===== */}
              {activeTab === "payments" && <div style={{
            background: "#FFFFFF",
            borderRadius: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}>
                  
                  <div style={{
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              background: "#E3F2FD"
            }}>
                    <div style={{
                position: "relative",
                minWidth: 260
              }}>
                      <i className="fas fa-search" style={{
                  position: "absolute",
                  left: 12,
                  top: 12,
                  color: "#2196F3",
                  fontSize: 13
                }}></i>
                      <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search employee name or role..." style={{
                  background: "#FFFFFF",
                  borderRadius: 8,
                  padding: "8px 12px 8px 34px",
                  fontSize: 12,
                  width: "100%",
                  outline: "none",
                  color: "#0D47A1",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }} />
                    </div>
                    
                    <div style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap"
              }}>
                      <div style={{
                  display: "flex",
                  gap: 6
                }}>
                        {["All", "Worker", "Staff"].map(t => <button key={t} onClick={() => setTypeFilter(t)} style={{
                    background: typeFilter === t ? "#0D47A1" : "#FFFFFF",
                    color: typeFilter === t ? "#FFFFFF" : "#0D47A1",
                    border: "none",
                    borderRadius: 20,
                    padding: "6px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                  }}>
                            {t === "All" ? "All" : t === "Worker" ? "Workers" : "Staff"}
                          </button>)}
                      </div>

                      <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{
                  background: "#FFFFFF",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#0D47A1",
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }}>
                        <option value="All">All Status</option>
                        <option value="Paid">Paid Only</option>
                        <option value="Unpaid">Unpaid Only</option>
                      </select>
                    </div>
                  </div>

                  {selectedEmpIds.length > 0 && <div style={{
              background: "#0D47A1",
              padding: "12px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
                      <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#FFFFFF"
              }}>
                        <i className="fas fa-check-square" style={{
                  marginRight: 6
                }}></i>
                        {selectedEmpIds.length} employee{selectedEmpIds.length > 1 ? "s" : ""} selected for payment
                      </div>
                      <div style={{
                display: "flex",
                gap: 10
              }}>
                        <button onClick={() => setSelectedEmpIds([])} style={{
                  background: "#FFFFFF",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#0D47A1",
                  cursor: "pointer"
                }}>
                          Clear Selection
                        </button>
                        <button onClick={() => setShowBulkPayModal(true)} style={{
                  background: "#FFFFFF",
                  color: "#0D47A1",
                  borderRadius: 8,
                  padding: "6px 16px",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6
                }}>
                          <i className="fas fa-money-check-alt"></i> Pay Selected (₹{employees.filter(emp => selectedEmpIds.includes(emp._id.toString())).reduce((sum, emp) => sum + (emp.calculatedSalary || emp.baseSalary || 0), 0).toLocaleString("en-IN")})
                        </button>
                      </div>
                    </div>}

                  {filteredEmployees.length === 0 ? <div style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#2196F3"
            }}>
                      <i className="fas fa-users-slash" style={{
                fontSize: 32,
                marginBottom: 12,
                color: "#2196F3"
              }}></i>
                      <div style={{
                fontSize: 13,
                fontWeight: 600
              }}>No employees found matching the filters</div>
                    </div> : <div style={{
              overflowX: "auto"
            }}>
                      <table style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left"
              }}>
                        <thead>
                          <tr style={{
                    background: "#0D47A1"
                  }}>
                            <th style={{
                      padding: "14px 20px",
                      width: 40
                    }}>
                              <input type="checkbox" checked={filteredEmployees.length > 0 && filteredEmployees.filter(emp => emp.status === "Unpaid").every(emp => selectedEmpIds.includes(emp._id.toString()))} onChange={e => {
                        if (e.target.checked) {
                          const unpaidIds = filteredEmployees.filter(emp => emp.status === "Unpaid").map(emp => emp._id.toString());
                          setSelectedEmpIds(unpaidIds);
                        } else {
                          setSelectedEmpIds([]);
                        }
                      }} style={{
                        cursor: "pointer",
                        accentColor: "#FFFFFF"
                      }} />
                            </th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Employee Details</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Category</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Base Salary</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Attendance</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Calculated Salary</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Status</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEmployees.map(emp => {
                    const attendance = emp.attendance || {
                      present: 0,
                      late: 0,
                      leave: 0,
                      absent: 0
                    };
                    const deductions = emp.deductions || {
                      totalDeductions: 0
                    };
                    return <tr key={emp._id} className="t-row" style={{
                      borderBottom: "1px solid #E3F2FD"
                    }}>
                                <td style={{
                        padding: "12px 20px"
                      }}>
                                  <input type="checkbox" disabled={emp.status === "Paid"} checked={selectedEmpIds.includes(emp._id.toString())} onChange={e => {
                          const idStr = emp._id.toString();
                          if (e.target.checked) {
                            setSelectedEmpIds(prev => [...prev, idStr]);
                          } else {
                            setSelectedEmpIds(prev => prev.filter(id => id !== idStr));
                          }
                        }} style={{
                          cursor: emp.status === "Paid" ? "not-allowed" : "pointer",
                          accentColor: "#0D47A1"
                        }} />
                                </td>
                                <td style={{
                        padding: "12px 20px"
                      }}>
                                  <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12
                        }}>
                                    <div style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            background: emp.type === "Staff" ? "#0D47A1" : "#2196F3",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: 14
                          }}>
                                      {emp.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                      <div style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#0D47A1"
                            }}>{emp.name}</div>
                                      <div style={{
                              fontSize: 11,
                              color: "#2196F3",
                              marginTop: 2
                            }}>{emp.role}</div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{
                        padding: "12px 20px"
                      }}>
                                  <span className={emp.type === "Staff" ? "badge-staff" : "badge-worker"} style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "3px 8px",
                          borderRadius: 20
                        }}>
                                    {emp.type}
                                  </span>
                                </td>
                                <td style={{
                        padding: "12px 20px",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#0D47A1"
                      }}>
                                  ₹{(emp.baseSalary || 0).toLocaleString("en-IN")}
                                </td>
                                <td style={{
                        padding: "12px 20px"
                      }}>
                                  <div style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2
                        }}>
                                    <div>
                                      <span className="attendance-badge attendance-present">{attendance.present || 0}P</span>
                                      <span className="attendance-badge attendance-late">{attendance.late || 0}L</span>
                                      <span className="attendance-badge attendance-leave">{attendance.leave || 0}LV</span>
                                      <span className="attendance-badge attendance-absent">{attendance.absent || 0}A</span>
                                    </div>
                                    <button onClick={() => fetchAttendanceDetails(emp._id, emp.type)} style={{
                            background: "transparent",
                            border: "none",
                            color: "#0D47A1",
                            fontSize: 10,
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline"
                          }}>
                                      View Details <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" />
                                    </button>
                                  </div>
                                </td>
                                <td style={{
                        padding: "12px 20px"
                      }}>
                                  <div style={{
                          display: "flex",
                          flexDirection: "column"
                        }}>
                                    <span className="calculated-salary">
                                      ₹{(emp.calculatedSalary || emp.baseSalary || 0).toLocaleString("en-IN")}
                                    </span>
                                    {deductions.totalDeductions > 0 && <span style={{
                            fontSize: 10,
                            color: "#0D47A1"
                          }}>
                                        -₹{Number(deductions.totalDeductions).toLocaleString("en-IN")} deducted
                                      </span>}
                                  </div>
                                </td>
                                <td style={{
                        padding: "12px 20px"
                      }}>
                                  <span className={emp.status === "Paid" ? "badge-paid" : "badge-unpaid"} style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "3px 8px",
                          borderRadius: 20,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4
                        }}>
                                    <span style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: emp.status === "Paid" ? "#FFFFFF" : "#0D47A1"
                          }} />
                                    {emp.status}
                                  </span>
                                </td>
                                <td style={{
                        padding: "12px 20px"
                      }}>
                                  {emp.status === "Paid" ? <div style={{
                          display: "flex",
                          gap: 6,
                          flexWrap: "wrap"
                        }}>
                                      <button onClick={() => setPayslipData(emp)} style={{
                            background: "#0D47A1",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "6px 12px",
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5
                          }}>
                                        <i className="fas fa-file-invoice-dollar"></i> Slip
                                      </button>
                                      <button onClick={() => handleWhatsAppShare(emp)} className="whatsapp-share-btn" style={{
                            padding: "6px 10px",
                            fontSize: 11
                          }}>
                                        <i className="fab fa-whatsapp"></i>
                                      </button>
                                    </div> : <button onClick={() => {
                          setPayModalData(emp);
                          setPayAmount(emp.calculatedSalary || "0");
                        }} style={{
                          background: "#0D47A1",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          padding: "6px 12px",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5
                        }}>
                                      <i className="fas fa-hand-holding-usd"></i> Pay
                                    </button>}
                                </td>
                              </tr>;
                  })}
                        </tbody>
                      </table>
                    </div>}
                </div>}

              {/* ===== TAB 2: HISTORY ===== */}
              {activeTab === "history" && <div style={{
            background: "#FFFFFF",
            borderRadius: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}>
                  <div style={{
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              background: "#E3F2FD"
            }}>
                    <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                      <i className="fas fa-receipt" style={{
                  color: "#0D47A1"
                }}></i>
                      <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0D47A1"
                }}>Disbursement Log</div>
                    </div>
                    <div style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap"
              }}>
                      <div style={{
                  display: "flex",
                  gap: 6
                }}>
                        {["All", "Worker", "Staff"].map(t => <button key={t} onClick={() => setHistoryTypeFilter(t)} style={{
                    background: historyTypeFilter === t ? "#0D47A1" : "#FFFFFF",
                    color: historyTypeFilter === t ? "#FFFFFF" : "#0D47A1",
                    border: "none",
                    borderRadius: 20,
                    padding: "6px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                  }}>
                            {t === "All" ? "All" : t === "Worker" ? "Workers" : "Staff"}
                          </button>)}
                      </div>
                      <button onClick={fetchAllTransactions} style={{
                  background: "#FFFFFF",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#0D47A1",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }}>
                        <i className="fas fa-sync-alt" style={{
                    marginRight: 4
                  }}></i> Refresh All
                      </button>
                    </div>
                  </div>

                  {filteredTransactions.length === 0 ? <div style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#2196F3"
            }}>
                      <i className="fas fa-history" style={{
                fontSize: 32,
                marginBottom: 12,
                color: "#2196F3"
              }}></i>
                      <div style={{
                fontSize: 13,
                fontWeight: 600
              }}>No transaction records found</div>
                    </div> : <div style={{
              overflowX: "auto"
            }}>
                      <table style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left"
              }}>
                        <thead>
                          <tr style={{
                    background: "#0D47A1"
                  }}>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Tx Date</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Employee</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Role / Type</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Paid Month</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Amount Paid</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Method</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Tx Ref ID</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTransactions.map(tx => <tr key={tx._id} className="t-row" style={{
                    borderBottom: "1px solid #E3F2FD"
                  }}>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 11,
                      color: "#2196F3"
                    }}>
                                {new Date(tx.paymentDate).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <div style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#0D47A1"
                      }}>{tx.employeeName}</div>
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <div style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#0D47A1"
                      }}>{tx.employeeRole}</div>
                                <span className={tx.employeeType === "Staff" ? "badge-staff" : "badge-worker"} style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: "1px 6px",
                        borderRadius: 10,
                        marginTop: 4,
                        display: "inline-block"
                      }}>
                                  {tx.employeeType}
                                </span>
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#0D47A1"
                    }}>
                                {formatMonth(tx.month)}
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0D47A1"
                    }}>
                                ₹{tx.paidAmount.toLocaleString("en-IN")}
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#2196F3"
                    }}>
                                <span style={{
                        padding: "2px 6px",
                        background: "#E3F2FD",
                        borderRadius: 6
                      }}>
                                  {tx.paymentMethod}
                                </span>
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 11,
                      fontFamily: "monospace",
                      color: "#2196F3"
                    }}>
                                {tx.transactionId}
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <div style={{
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap"
                      }}>
                                  <button onClick={() => setPayslipData({
                          _id: tx.employeeId,
                          name: tx.employeeName,
                          role: tx.employeeRole,
                          type: tx.employeeType,
                          phone: tx.employeePhone || '',
                          baseSalary: tx.baseSalary,
                          calculatedSalary: tx.paidAmount,
                          attendance: tx.attendance || {
                            present: 0,
                            late: 0,
                            leave: 0,
                            absent: 0
                          },
                          deductions: tx.deductions || {
                            lateDeduction: 0,
                            leaveDeduction: 0,
                            absentDeduction: 0,
                            totalDeductions: 0
                          },
                          paymentDetails: {
                            transactionId: tx.transactionId,
                            paidAmount: tx.paidAmount,
                            paymentDate: tx.paymentDate,
                            paymentMethod: tx.paymentMethod,
                            remarks: tx.remarks
                          }
                        })} style={{
                          background: "#E3F2FD",
                          borderRadius: 8,
                          padding: "5px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#0D47A1",
                          cursor: "pointer"
                        }} title="View Payslip">
                                    <i className="fas fa-file-invoice-dollar" style={{
                            color: "#0D47A1"
                          }}></i> Slip
                                  </button>
                                </div>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>}

              {/* ===== TAB 3: CONFIG ===== */}
              {activeTab === "config" && <div style={{
            background: "#FFFFFF",
            borderRadius: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}>
                  <div style={{
              padding: "16px 20px",
              background: "#E3F2FD",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
                    <div>
                      <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0D47A1"
                }}>Salary Configuration</div>
                      <div style={{
                  fontSize: 11,
                  color: "#2196F3",
                  marginTop: 2
                }}>Define the basic monthly salary package for all staff and workers.</div>
                    </div>
                  </div>

                  {employees.length === 0 ? <div style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#2196F3"
            }}>
                      <div style={{
                fontSize: 13,
                fontWeight: 600
              }}>No employee records found. Create employees in Staff/Workers Management first.</div>
                    </div> : <div style={{
              overflowX: "auto"
            }}>
                      <table style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left"
              }}>
                        <thead>
                          <tr style={{
                    background: "#0D47A1"
                  }}>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Employee</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Role</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Employee Category</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Basic Monthly Salary</th>
                            <th style={{
                      padding: "14px 20px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employees.map(emp => <tr key={emp._id} className="t-row" style={{
                    borderBottom: "1px solid #E3F2FD"
                  }}>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <div style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#0D47A1"
                      }}>{emp.name}</div>
                                <div style={{
                        fontSize: 11,
                        color: "#2196F3",
                        marginTop: 2
                      }}>{emp.phone || "No phone"}</div>
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#0D47A1"
                    }}>
                                {emp.role}
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <span className={emp.type === "Staff" ? "badge-staff" : "badge-worker"} style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 20
                      }}>
                                  {emp.type}
                                </span>
                              </td>
                              <td style={{
                      padding: "12px 20px",
                      fontSize: 14,
                      fontWeight: 800,
                      color: "#0D47A1"
                    }}>
                                ₹{(emp.baseSalary || 0).toLocaleString("en-IN")}
                              </td>
                              <td style={{
                      padding: "12px 20px"
                    }}>
                                <button onClick={() => {
                        setEditSalaryData(emp);
                        setNewSalary(emp.baseSalary || "");
                      }} style={{
                        background: "#FFFFFF",
                        color: "#0D47A1",
                        borderRadius: 8,
                        padding: "6px 12px",
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                      }}>
                                  <i className="fas fa-edit"></i> Edit Salary
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>}
            </>}
        </div>
      </div>

      {/* ===== MODALS ===== */}

      {/* Attendance Details Modal */}
      {attendanceDetailData && <div style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(13,71,161,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(4px)"
    }}>
          <div style={{
        background: "#FFFFFF",
        borderRadius: 16,
        width: "min(540px, 95%)",
        maxHeight: "80vh",
        overflow: "hidden",
        boxShadow: "0 20px 50px rgba(0,0,0,0.15)"
      }}>
            <div className="blue-gradient" style={{
          padding: "16px 20px",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
              <div style={{
            fontWeight: 700,
            fontSize: 15,
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
                <i className="fas fa-clipboard-list"></i> Attendance & Salary Breakdown
              </div>
              <button onClick={() => setAttendanceDetailData(null)} style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            fontSize: 18
          }}>&times;</button>
            </div>
            <div style={{
          padding: 20,
          overflowY: "auto",
          maxHeight: "calc(80vh - 60px)"
        }}>
              <div style={{
            background: "#E3F2FD",
            padding: "12px 14px",
            borderRadius: 10,
            marginBottom: 16
          }}>
                <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#0D47A1"
            }}>{attendanceDetailData.employeeName}</div>
                <div style={{
              fontSize: 11,
              color: "#2196F3"
            }}>{attendanceDetailData.employeeType} · {formatMonth(attendanceDetailData.month)}</div>
              </div>

              <div style={{
            marginBottom: 16
          }}>
                <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#0D47A1",
              marginBottom: 8
            }}><BarChart3 className="inline w-[1em] h-[1em] align-[-0.125em]" /> ATTENDANCE SUMMARY</div>
                <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: 8
            }}>
                  <div style={{
                background: "#0D47A1",
                padding: "8px",
                borderRadius: 8,
                textAlign: "center"
              }}>
                    <div style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#FFFFFF"
                }}>{attendanceDetailData.attendance?.present || 0}</div>
                    <div style={{
                  fontSize: 9,
                  color: "#90CAF9"
                }}>Present</div>
                  </div>
                  <div style={{
                background: "#E3F2FD",
                padding: "8px",
                borderRadius: 8,
                textAlign: "center"
              }}>
                    <div style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#2196F3"
                }}>{attendanceDetailData.attendance?.late || 0}</div>
                    <div style={{
                  fontSize: 9,
                  color: "#2196F3"
                }}>Late</div>
                  </div>
                  <div style={{
                background: "#E3F2FD",
                padding: "8px",
                borderRadius: 8,
                textAlign: "center"
              }}>
                    <div style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#2196F3"
                }}>{attendanceDetailData.attendance?.leave || 0}</div>
                    <div style={{
                  fontSize: 9,
                  color: "#2196F3"
                }}>Leave</div>
                  </div>
                  <div style={{
                background: "#E3F2FD",
                padding: "8px",
                borderRadius: 8,
                textAlign: "center"
              }}>
                    <div style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#2196F3"
                }}>{attendanceDetailData.attendance?.absent || 0}</div>
                    <div style={{
                  fontSize: 9,
                  color: "#2196F3"
                }}>Absent</div>
                  </div>
                </div>
              </div>

              <div style={{
            marginBottom: 16
          }}>
                <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#0D47A1",
              marginBottom: 8
            }}><Coins className="inline w-[1em] h-[1em] align-[-0.125em]" /> SALARY CALCULATION</div>
                <div style={{
              background: "#E3F2FD",
              borderRadius: 10,
              padding: 12
            }}>
                  <div className="deduction-row">
                    <span>Base Monthly Salary</span>
                    <span>₹{Number(attendanceDetailData.baseSalary).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="deduction-row">
                    <span>Daily Salary Rate</span>
                    <span>₹{Number(attendanceDetailData.dailySalary).toLocaleString("en-IN")}</span>
                  </div>
                  <div style={{
                marginTop: 8,
                borderTop: "1px solid #90CAF9",
                paddingTop: 8
              }}>
                    <div style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#0D47A1",
                  marginBottom: 6
                }}>Deductions:</div>
                    <div className="deduction-row">
                      <span>Late Arrivals ({attendanceDetailData.deductions?.lateCount || 0} days × 10% daily)</span>
                      <span style={{
                    color: "#0D47A1"
                  }}>-₹{Number(attendanceDetailData.deductions?.lateDeduction || 0).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="deduction-row">
                      <span>Leave Days ({attendanceDetailData.deductions?.leaveCount || 0} days × 100%)</span>
                      <span style={{
                    color: "#0D47A1"
                  }}>-₹{Number(attendanceDetailData.deductions?.leaveDeduction || 0).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="deduction-row">
                      <span>Absent Days ({attendanceDetailData.deductions?.absentCount || 0} days × 100%)</span>
                      <span style={{
                    color: "#0D47A1"
                  }}>-₹{Number(attendanceDetailData.deductions?.absentDeduction || 0).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="deduction-row deduction-total">
                      <span>Total Deductions</span>
                      <span>-₹{Number(attendanceDetailData.deductions?.totalDeductions || 0).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                  <div style={{
                marginTop: 8,
                borderTop: "2px solid #0D47A1",
                paddingTop: 8
              }}>
                    <div className="deduction-row" style={{
                  fontWeight: 700,
                  fontSize: 14
                }}>
                      <span>Net Calculated Salary</span>
                      <span style={{
                    color: "#0D47A1"
                  }}>₹{Number(attendanceDetailData.calculatedSalary).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </div>
              </div>

              {attendanceDetailData.approvedLeaveRequests?.length > 0 && <div style={{
            marginBottom: 16
          }}>
                  <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#0D47A1",
              marginBottom: 6
            }}><ClipboardList className="inline w-[1em] h-[1em] align-[-0.125em]" /> Approved Leave Requests</div>
                  {attendanceDetailData.approvedLeaveRequests.map((leave, idx) => <div key={idx} style={{
              background: "#E3F2FD",
              padding: "6px 10px",
              borderRadius: 6,
              marginBottom: 4,
              fontSize: 11
            }}>
                      <span style={{
                fontWeight: 600
              }}>{new Date(leave.date).toLocaleDateString("en-IN")}</span> - {leave.reason || "No reason provided"}
                      {leave.adminRemark && <span style={{
                color: "#2196F3",
                display: "block",
                fontSize: 10
              }}><FileEdit className="inline w-[1em] h-[1em] align-[-0.125em]" /> {leave.adminRemark}</span>}
                    </div>)}
                </div>}

              <button onClick={() => setAttendanceDetailData(null)} style={{
            width: "100%",
            padding: 10,
            background: "#E3F2FD",
            border: "none",
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 600,
            color: "#0D47A1",
            cursor: "pointer"
          }}>
                Close
              </button>
            </div>
          </div>
        </div>}

      {/* Bulk Payment Modal */}
      {showBulkPayModal && <div style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(13,71,161,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(4px)"
    }}>
          <div style={{
        background: "#FFFFFF",
        borderRadius: 16,
        width: "min(485px, 95%)",
        overflow: "hidden",
        boxShadow: "0 20px 50px rgba(0,0,0,0.15)"
      }}>
            <div className="blue-gradient" style={{
          padding: "16px 20px",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
              <div style={{
            fontWeight: 700,
            fontSize: 15,
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
                <i className="fas fa-money-check-alt"></i> Bulk Salary Payment
              </div>
              <button onClick={() => setShowBulkPayModal(false)} style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            fontSize: 18
          }}>&times;</button>
            </div>
            
            <form onSubmit={handleBulkPaySubmit} style={{
          padding: 20
        }}>
              <div style={{
            background: "#E3F2FD",
            padding: "12px 14px",
            borderRadius: 10,
            marginBottom: 16,
            maxHeight: 150,
            overflowY: "auto"
          }}>
                <div style={{
              fontSize: 10,
              color: "#2196F3",
              textTransform: "uppercase",
              fontWeight: 700,
              marginBottom: 6
            }}>Selected Employees ({selectedEmpIds.length})</div>
                {employees.filter(emp => selectedEmpIds.includes(emp._id.toString())).map(emp => <div key={emp._id} style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
              padding: "4px 0"
            }}>
                    <span style={{
                fontWeight: 600,
                color: "#0D47A1"
              }}>{emp.name} ({emp.role})</span>
                    <span style={{
                fontWeight: 700,
                color: "#0D47A1"
              }}>₹{Number(emp.calculatedSalary || emp.baseSalary || 0).toLocaleString("en-IN")}</span>
                  </div>)}
              </div>

              <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 16
          }}>
                <div>
                  <label style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#0D47A1",
                display: "block",
                marginBottom: 6
              }}>Payment Month</label>
                  <input type="text" value={formatMonth(selectedMonth)} readOnly style={{
                width: "100%",
                padding: 10,
                borderRadius: 8,
                background: "#E3F2FD",
                fontSize: 12,
                fontWeight: 600,
                color: "#2196F3",
                outline: "none"
              }} />
                </div>
                <div>
                  <label style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#0D47A1",
                display: "block",
                marginBottom: 6
              }}>Total Payroll (₹)</label>
                  <input type="text" value={`₹${employees.filter(emp => selectedEmpIds.includes(emp._id.toString())).reduce((sum, emp) => sum + (emp.calculatedSalary || emp.baseSalary || 0), 0).toLocaleString("en-IN")}`} readOnly style={{
                width: "100%",
                padding: 10,
                borderRadius: 8,
                background: "#E3F2FD",
                fontSize: 12,
                fontWeight: 700,
                color: "#0D47A1",
                outline: "none"
              }} />
                </div>
              </div>

              <div style={{
            marginBottom: 16
          }}>
                <label style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#0D47A1",
              display: "block",
              marginBottom: 6
            }}>Payment Mode *</label>
                <select value={bulkPayMethod} onChange={e => setBulkPayMethod(e.target.value)} style={{
              width: "100%",
              padding: 10,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              color: "#0D47A1",
              outline: "none",
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="UPI">UPI / GPay / PhonePe</option>
                  <option value="Cash">Cash</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>

              <div style={{
            marginBottom: 20
          }}>
                <label style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#0D47A1",
              display: "block",
              marginBottom: 6
            }}>Remarks</label>
                <textarea value={bulkPayRemarks} onChange={e => setBulkPayRemarks(e.target.value)} placeholder="Any bulk payment remarks..." rows={2} style={{
              width: "100%",
              padding: 10,
              borderRadius: 8,
              fontSize: 12,
              color: "#0D47A1",
              outline: "none",
              resize: "none",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }} />
              </div>

              <div style={{
            display: "flex",
            gap: 10
          }}>
                <button type="button" onClick={() => setShowBulkPayModal(false)} style={{
              flex: 1,
              padding: 10,
              background: "#E3F2FD",
              color: "#2196F3",
              border: "none",
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer"
            }}>
                  Cancel
                </button>
                <button type="submit" disabled={processingBulkPay} style={{
              flex: 2,
              padding: 10,
              background: processingBulkPay ? "#2196F3" : "#0D47A1",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 700,
              cursor: processingBulkPay ? "not-allowed" : "pointer"
            }}>
                  {processingBulkPay ? "Processing..." : "Confirm Bulk Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>}

      {/* Pay Modal */}
      {payModalData && <div style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(13,71,161,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(4px)"
    }}>
          <div style={{
        background: "#FFFFFF",
        borderRadius: 16,
        width: "min(680px, 96%)",
        maxHeight: "90vh",
        overflow: "hidden",
        boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column"
      }}>
            <div className="blue-gradient" style={{
          padding: "16px 20px",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0
        }}>
              <div style={{
            fontWeight: 700,
            fontSize: 15,
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
                <i className="fas fa-hand-holding-usd"></i> Record Salary Payment
              </div>
              <button onClick={() => setPayModalData(null)} style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            fontSize: 18
          }}>&times;</button>
            </div>
            
            <form onSubmit={handlePaySubmit} style={{
          overflowY: "auto"
        }}>
              <div className="pay-modal-body">

                {/* ===== SIDEBAR ===== */}
                <aside className="pay-modal-sidebar">
                  <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: payModalData.type === "Staff" ? "#0D47A1" : "#2196F3",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 20,
                marginBottom: 10
              }}>
                    {payModalData.name ? payModalData.name.charAt(0).toUpperCase() : "?"}
                  </div>

                  <div style={{
                fontSize: 10,
                color: "#2196F3",
                textTransform: "uppercase",
                fontWeight: 700
              }}>Paying Employee</div>
                  <div style={{
                fontSize: 15,
                fontWeight: 800,
                color: "#0D47A1",
                marginTop: 4
              }}>{payModalData.name}</div>
                  <div style={{
                fontSize: 11,
                color: "#2196F3",
                marginTop: 2
              }}>{payModalData.role}</div>
                  <span className={payModalData.type === "Staff" ? "badge-staff" : "badge-worker"} style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "3px 8px",
                borderRadius: 20,
                display: "inline-block",
                marginTop: 8
              }}>
                    {payModalData.type}
                  </span>

                  <div style={{
                marginTop: 18,
                paddingTop: 14,
                borderTop: "1px solid #90CAF9"
              }}>
                    <div style={{
                  fontSize: 10,
                  color: "#2196F3",
                  textTransform: "uppercase",
                  fontWeight: 700
                }}>Payment Month</div>
                    <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0D47A1",
                  marginTop: 4
                }}>{formatMonth(selectedMonth)}</div>
                  </div>

                  <div style={{
                marginTop: 14
              }}>
                    <div style={{
                  fontSize: 10,
                  color: "#2196F3",
                  textTransform: "uppercase",
                  fontWeight: 700
                }}>Base Salary</div>
                    <div style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: "#0D47A1",
                  marginTop: 4
                }}>₹{Number(payModalData.baseSalary).toLocaleString("en-IN")}</div>
                  </div>

                  {payModalData.calculatedSalary != null && <div style={{
                marginTop: 14
              }}>
                      <div style={{
                  fontSize: 10,
                  color: "#2196F3",
                  textTransform: "uppercase",
                  fontWeight: 700
                }}>Calculated Salary</div>
                      <div style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: "#0D47A1",
                  marginTop: 4
                }}>₹{Number(payModalData.calculatedSalary).toLocaleString("en-IN")}</div>
                    </div>}

                  {payModalData.deductions?.totalDeductions > 0 && <div style={{
                marginTop: 14,
                paddingTop: 14,
                borderTop: "1px solid #90CAF9"
              }}>
                      <div style={{
                  fontSize: 10,
                  color: "#2196F3",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  marginBottom: 4
                }}>
                        <i className="fas fa-info-circle"></i> Deductions Applied
                      </div>
                      <div style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#0D47A1"
                }}>
                        -₹{Number(payModalData.deductions.totalDeductions).toLocaleString("en-IN")}
                      </div>
                      <div style={{
                  fontSize: 10,
                  color: "#2196F3",
                  marginTop: 4,
                  lineHeight: 1.5
                }}>
                        Late: {payModalData.deductions.lateCount || 0} days<br />
                        Leave: {payModalData.deductions.leaveCount || 0} days<br />
                        Absent: {payModalData.deductions.absentCount || 0} days
                      </div>
                    </div>}
                </aside>

                {/* ===== MAIN FORM ===== */}
                <div className="pay-modal-main">
                  <div style={{
                marginBottom: 16
              }}>
                    <label style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#0D47A1",
                  display: "block",
                  marginBottom: 6
                }}>
                      Paid Salary Amount (₹) *
                      {payModalData.calculatedSalary && <span style={{
                    fontWeight: 400,
                    color: "#2196F3",
                    marginLeft: 6
                  }}>
                          (Calculated: ₹{Number(payModalData.calculatedSalary).toLocaleString("en-IN")})
                        </span>}
                    </label>
                    <input type="number" required value={payAmount} onChange={e => setPayAmount(e.target.value)} placeholder="Enter amount to disburse" style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0D47A1",
                  outline: "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }} />
                  </div>

                  <div style={{
                marginBottom: 16
              }}>
                    <label style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#0D47A1",
                  display: "block",
                  marginBottom: 6
                }}>Payment Mode *</label>
                    <select value={payMethod} onChange={e => setPayMethod(e.target.value)} style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#0D47A1",
                  outline: "none",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }}>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="UPI">UPI / GPay / PhonePe</option>
                      <option value="Cash">Cash</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>

                  <div style={{
                marginBottom: 16
              }}>
                    <label style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#0D47A1",
                  display: "block",
                  marginBottom: 6
                }}>Tx Ref Number (Optional)</label>
                    <input type="text" value={payTxId} onChange={e => setPayTxId(e.target.value)} placeholder="e.g. UTR / Bank Reference ID" style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#0D47A1",
                  outline: "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }} />
                  </div>

                  <div style={{
                marginBottom: 20
              }}>
                    <label style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#0D47A1",
                  display: "block",
                  marginBottom: 6
                }}>Remarks</label>
                    <textarea value={payRemarks} onChange={e => setPayRemarks(e.target.value)} placeholder="Any payment remarks..." rows={2} style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#0D47A1",
                  outline: "none",
                  resize: "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }} />
                  </div>

                  <div style={{
                display: "flex",
                gap: 10
              }}>
                    <button type="button" onClick={() => setPayModalData(null)} style={{
                  flex: 1,
                  padding: 10,
                  background: "#E3F2FD",
                  color: "#2196F3",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer"
                }}>
                      Cancel
                    </button>
                    <button type="submit" disabled={processingPayment} style={{
                  flex: 2,
                  padding: 10,
                  background: processingPayment ? "#2196F3" : "#0D47A1",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: processingPayment ? "not-allowed" : "pointer"
                }}>
                      {processingPayment ? "Recording Payment..." : "Record Payment"}
                    </button>
                  </div>
                </div>

              </div>
            </form>
          </div>
        </div>}

      {/* Edit Salary Modal */}
      {editSalaryData && <div style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(13,71,161,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(4px)"
    }}>
          <div style={{
        background: "#FFFFFF",
        borderRadius: 16,
        width: "min(380px, 95%)",
        overflow: "hidden",
        boxShadow: "0 20px 50px rgba(0,0,0,0.15)"
      }}>
            <div className="blue-gradient" style={{
          padding: "16px 20px",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
              <div style={{
            fontWeight: 700,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
                <i className="fas fa-cog"></i> Configure Salary Logic
              </div>
              <button onClick={() => setEditSalaryData(null)} style={{
            background: "transparent",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            fontSize: 18
          }}>&times;</button>
            </div>
            
            <form onSubmit={handleSalarySave} style={{
          padding: 20
        }}>
              <div style={{
            background: "#E3F2FD",
            padding: "12px 14px",
            borderRadius: 10,
            marginBottom: 16
          }}>
                <div style={{
              fontSize: 10,
              color: "#2196F3",
              textTransform: "uppercase",
              fontWeight: 700
            }}>Employee Name</div>
                <div style={{
              fontSize: 13,
              fontWeight: 800,
              color: "#0D47A1",
              marginTop: 4
            }}>{editSalaryData.name}</div>
                <div style={{
              fontSize: 11,
              color: "#2196F3",
              marginTop: 2
            }}>{editSalaryData.role} &middot; <span style={{
                fontWeight: 600
              }}>{editSalaryData.type}</span></div>
              </div>

              <div style={{
            marginBottom: 20
          }}>
                <label style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#0D47A1",
              display: "block",
              marginBottom: 8
            }}>Monthly Base Salary (₹) *</label>
                <div style={{
              position: "relative"
            }}>
                  <span style={{
                position: "absolute",
                left: 12,
                top: 10,
                fontWeight: 700,
                color: "#2196F3",
                fontSize: 14
              }}>₹</span>
                  <input type="number" required value={newSalary} onChange={e => setNewSalary(e.target.value)} placeholder="e.g. 25000" style={{
                width: "100%",
                padding: "10px 10px 10px 24px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 700,
                color: "#0D47A1",
                outline: "none",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }} />
                </div>
                <span style={{
              fontSize: 10,
              color: "#2196F3",
              marginTop: 6,
              display: "block"
            }}>This setting determines the base salary amount for monthly payroll generation.</span>
              </div>

              <div style={{
            display: "flex",
            gap: 10
          }}>
                <button type="button" onClick={() => setEditSalaryData(null)} style={{
              flex: 1,
              padding: 10,
              background: "#E3F2FD",
              color: "#2196F3",
              border: "none",
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer"
            }}>
                  Cancel
                </button>
                <button type="submit" disabled={savingSalary} style={{
              flex: 1.5,
              padding: 10,
              background: savingSalary ? "#2196F3" : "#0D47A1",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 10,
              fontSize: 12,
              fontWeight: 700,
              cursor: savingSalary ? "not-allowed" : "pointer"
            }}>
                  {savingSalary ? "Saving..." : "Save Config"}
                </button>
              </div>
            </form>
          </div>
        </div>}

      {/* ===== PAYSLIP MODAL WITH WHATSAPP SHARE BUTTON ===== */}
      {payslipData && <div style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(13,71,161,0.65)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(6px)"
    }}>
          <div style={{
        background: "#FFFFFF",
        borderRadius: 20,
        width: "min(600px, 95%)",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 25px 70px rgba(0,0,0,0.15)"
      }}>
            
            {/* Modal Header with WhatsApp Share Button */}
            <div style={{
          background: "#0D47A1",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
          color: "#FFFFFF"
        }}>
              <span style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.5px"
          }}>
                <i className="fas fa-file-invoice-dollar" style={{
              marginRight: 6
            }}></i> PAYSLIP RECEIPT
              </span>
              <div style={{
            display: "flex",
            gap: 8,
            alignItems: "center"
          }}>
                <button onClick={() => {
              const empData = payslipData;
              setPayslipData(null);
              setTimeout(() => {
                handleWhatsAppShare(empData);
              }, 300);
            }} className="whatsapp-share-btn" style={{
              background: "#25D366",
              color: "white",
              border: "none",
              padding: "6px 14px",
              borderRadius: "30px",
              fontSize: "11px",
              fontWeight: "600",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              boxShadow: "0 2px 8px rgba(37, 211, 102, 0.3)"
            }} title="Share via WhatsApp">
                  <i className="fab fa-whatsapp" style={{
                fontSize: "14px"
              }}></i> Share
                </button>
                <button onClick={() => window.print()} style={{
              background: "#2196F3",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "5px 12px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4
            }}>
                  <i className="fas fa-print"></i> Print
                </button>
                <button onClick={() => setPayslipData(null)} style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              color: "#fff",
              width: 26,
              height: 26,
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
                  <span style={{
                fontSize: 14,
                fontWeight: 700
              }}>&times;</span>
                </button>
              </div>
            </div>

            {/* Payslip Document Area */}
            <div style={{
          flex: 1,
          overflowY: "auto",
          padding: 24
        }}>
              <div id="payslip-print-area" style={{
            padding: 32,
            borderRadius: 12,
            backgroundColor: "#FFFFFF",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
                
                {/* Header */}
                <div style={{
              textAlign: "center",
              borderBottom: "2px double #2196F3",
              paddingBottom: 16
            }}>
                  <div style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#0D47A1",
                fontFamily: "'Poppins', sans-serif",
                letterSpacing: "1px"
              }}>{schoolName.toUpperCase()} SMART CAMPUS</div>
                  <div style={{
                fontSize: 10,
                color: "#2196F3",
                marginTop: 4
              }}>Affiliated Secondary &amp; Higher Secondary School</div>
                  <div style={{
                fontSize: 9,
                color: "#2196F3",
                marginTop: 2
              }}>Salem, Tamil Nadu, India | Contact: support@{getSchoolEmailDomain(schoolName)}</div>
                  <div style={{
                background: "#0D47A1",
                padding: "6px 12px",
                borderRadius: 6,
                display: "inline-block",
                marginTop: 12,
                fontSize: 11,
                fontWeight: 700,
                color: "#FFFFFF",
                letterSpacing: "0.5px"
              }}>
                    SALARY ADVICE SLIP &middot; {formatMonth(selectedMonth).toUpperCase()}
                  </div>
                </div>

                {/* Employee Details Table */}
                <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginTop: 20,
              fontSize: 11,
              color: "#0D47A1"
            }}>
                  <div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#2196F3"
                  }}>Employee Name:</span>
                      <span style={{
                    fontWeight: 700,
                    color: "#0D47A1"
                  }}>{payslipData.name}</span>
                    </div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#2196F3"
                  }}>Designation/Role:</span>
                      <span>{payslipData.role}</span>
                    </div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#2196F3"
                  }}>Employee Category:</span>
                      <span>{payslipData.type}</span>
                    </div>
                  </div>
                  <div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#2196F3"
                  }}>Salary Month:</span>
                      <span style={{
                    fontWeight: 600
                  }}>{formatMonth(selectedMonth)}</span>
                    </div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#2196F3"
                  }}>Receipt Date:</span>
                      <span>{payslipData.paymentDetails?.paymentDate ? new Date(payslipData.paymentDetails.paymentDate).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    }) : new Date().toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })}</span>
                    </div>
                    <div style={{
                  display: "flex",
                  padding: "4px 0"
                }}>
                      <span style={{
                    width: 130,
                    fontWeight: 600,
                    color: "#2196F3"
                  }}>Payment Mode:</span>
                      <span style={{
                    fontWeight: 600
                  }}>{payslipData.paymentDetails?.paymentMethod || "Bank Transfer"}</span>
                    </div>
                  </div>
                </div>

                {/* Attendance Summary */}
                {payslipData.attendance && <div style={{
              marginTop: 16,
              background: "#E3F2FD",
              padding: "10px 14px",
              borderRadius: 8
            }}>
                    <div style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#0D47A1",
                textTransform: "uppercase",
                marginBottom: 4
              }}>Attendance Summary</div>
                    <div style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap"
              }}>
                      <span style={{
                  fontSize: 11
                }}><CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> Present: <strong>{payslipData.attendance.present || 0}</strong></span>
                      <span style={{
                  fontSize: 11
                }}>⏰ Late: <strong>{payslipData.attendance.late || 0}</strong></span>
                      <span style={{
                  fontSize: 11
                }}><ClipboardList className="inline w-[1em] h-[1em] align-[-0.125em]" /> Leave: <strong>{payslipData.attendance.leave || 0}</strong></span>
                      <span style={{
                  fontSize: 11
                }}><XCircle className="inline w-[1em] h-[1em] align-[-0.125em]" /> Absent: <strong>{payslipData.attendance.absent || 0}</strong></span>
                    </div>
                  </div>}

                {/* Pay Details Table */}
                <table style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: 16,
              fontSize: 11
            }}>
                  <thead>
                    <tr style={{
                  background: "#0D47A1"
                }}>
                      <th style={{
                    padding: "8px 12px",
                    textAlign: "left",
                    color: "#FFFFFF"
                  }}>PAY DETAILS</th>
                      <th style={{
                    padding: "8px 12px",
                    textAlign: "right",
                    color: "#FFFFFF"
                  }}>AMOUNT (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{
                    padding: "10px 12px",
                    color: "#0D47A1"
                  }}>
                        <strong>Basic monthly base salary rate</strong>
                        <div style={{
                      fontSize: 9,
                      color: "#2196F3",
                      marginTop: 2
                    }}>Contractual basic wages structure</div>
                      </td>
                      <td style={{
                    padding: "10px 12px",
                    textAlign: "right",
                    fontWeight: 600
                  }}>
                        ₹{Number(payslipData.baseSalary || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>

                    {/* Deductions */}
                    {payslipData.deductions && payslipData.deductions.totalDeductions > 0 && <>
                        <tr style={{
                    background: "#E3F2FD"
                  }}>
                          <td style={{
                      padding: "10px 12px",
                      color: "#0D47A1"
                    }}>
                            <strong>Late Arrival Deduction</strong>
                            <div style={{
                        fontSize: 9,
                        color: "#2196F3",
                        marginTop: 2
                      }}>{payslipData.deductions.lateCount || 0} days × 10% daily</div>
                          </td>
                          <td style={{
                      padding: "10px 12px",
                      textAlign: "right",
                      color: "#0D47A1"
                    }}>
                            -₹{Number(payslipData.deductions.lateDeduction || 0).toLocaleString("en-IN")}
                          </td>
                        </tr>
                        <tr style={{
                    background: "#E3F2FD"
                  }}>
                          <td style={{
                      padding: "10px 12px",
                      color: "#0D47A1"
                    }}>
                            <strong>Leave Deduction</strong>
                            <div style={{
                        fontSize: 9,
                        color: "#2196F3",
                        marginTop: 2
                      }}>{payslipData.deductions.leaveCount || 0} days × 100% daily</div>
                          </td>
                          <td style={{
                      padding: "10px 12px",
                      textAlign: "right",
                      color: "#0D47A1"
                    }}>
                            -₹{Number(payslipData.deductions.leaveDeduction || 0).toLocaleString("en-IN")}
                          </td>
                        </tr>
                        <tr style={{
                    background: "#E3F2FD"
                  }}>
                          <td style={{
                      padding: "10px 12px",
                      color: "#0D47A1"
                    }}>
                            <strong>Absent Deduction</strong>
                            <div style={{
                        fontSize: 9,
                        color: "#2196F3",
                        marginTop: 2
                      }}>{payslipData.deductions.absentCount || 0} days × 100% daily</div>
                          </td>
                          <td style={{
                      padding: "10px 12px",
                      textAlign: "right",
                      color: "#0D47A1"
                    }}>
                            -₹{Number(payslipData.deductions.absentDeduction || 0).toLocaleString("en-IN")}
                          </td>
                        </tr>
                      </>}

                    {/* Net Amount */}
                    <tr>
                      <td style={{
                    padding: "10px 12px",
                    color: "#0D47A1"
                  }}>
                        <strong>Net Disbursed Salary</strong>
                        <div style={{
                      fontSize: 9,
                      color: "#2196F3",
                      marginTop: 2
                    }}>After all attendance deductions</div>
                      </td>
                      <td style={{
                    padding: "10px 12px",
                    textAlign: "right",
                    fontWeight: 700,
                    color: "#0D47A1"
                  }}>
                        ₹{Number(payslipData.paymentDetails?.paidAmount || payslipData.calculatedSalary || payslipData.baseSalary || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>

                    {/* Total Deductions Summary */}
                    {payslipData.deductions && payslipData.deductions.totalDeductions > 0 && <tr style={{
                  background: "#E3F2FD",
                  fontWeight: 700,
                  fontSize: 11
                }}>
                        <td style={{
                    padding: "10px 12px",
                    color: "#0D47A1"
                  }}>Total Deductions</td>
                        <td style={{
                    padding: "10px 12px",
                    textAlign: "right",
                    color: "#0D47A1"
                  }}>
                          -₹{Number(payslipData.deductions.totalDeductions || 0).toLocaleString("en-IN")}
                        </td>
                      </tr>}

                    <tr style={{
                  background: "#0D47A1",
                  fontWeight: 700,
                  fontSize: 12
                }}>
                      <td style={{
                    padding: "12px 12px",
                    color: "#FFFFFF"
                  }}>NET DISBURSED AMOUNT</td>
                      <td style={{
                    padding: "12px 12px",
                    textAlign: "right",
                    color: "#FFFFFF",
                    fontSize: 13
                  }}>
                        ₹{Number(payslipData.paymentDetails?.paidAmount || payslipData.calculatedSalary || payslipData.baseSalary || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Transaction Details */}
                <div style={{
              marginTop: 20,
              background: "#E3F2FD",
              borderRadius: 8,
              padding: "12px 16px",
              fontSize: 10,
              color: "#2196F3",
              fontFamily: "monospace"
            }}>
                  <div>TRANSACTION REFERENCE ID: {payslipData.paymentDetails?.transactionId || "N/A"}</div>
                  <div style={{
                marginTop: 4
              }}>STATUS: PAID &amp; SETTLED</div>
                  {payslipData.paymentDetails?.remarks && <div style={{
                marginTop: 6,
                fontStyle: "italic",
                fontFamily: "'Inter', sans-serif"
              }}>
                      <strong>Remarks:</strong> {payslipData.paymentDetails.remarks}
                    </div>}
                </div>

                {/* Signatures */}
                

                {/* Close Receipt Text */}
                <div style={{
              textAlign: "center",
              marginTop: 20,
              fontSize: 10,
              color: "#2196F3",
              borderTop: "1px solid #E3F2FD",
              paddingTop: 12
            }}>
                  CLOSED RECEIPT
                </div>

              </div>
            </div>

            {/* Bottom Close Panel */}
            <div style={{
          background: "#E3F2FD",
          padding: "12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0
        }}>
              <button onClick={() => {
            const empData = payslipData;
            setPayslipData(null);
            setTimeout(() => handleWhatsAppShare(empData), 300);
          }} className="whatsapp-share-btn" style={{
            background: "#25D366",
            color: "white",
            border: "none",
            padding: "8px 18px",
            borderRadius: "30px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 2px 8px rgba(37, 211, 102, 0.3)"
          }}>
                <i className="fab fa-whatsapp" style={{
              fontSize: "16px"
            }}></i> Share on WhatsApp
              </button>
              <button onClick={() => setPayslipData(null)} style={{
            padding: "8px 16px",
            background: "#FFFFFF",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            color: "#0D47A1",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
                Close Receipt
              </button>
            </div>

          </div>
        </div>}
    </>;
}